import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Form,
  Input,
  Button,
  Modal,
  Typography,
  Space,
  Alert,
  notification,
} from "antd";
import {
  UserOutlined,
  LockOutlined,
  ExclamationCircleFilled,
  MailOutlined,
  FieldNumberOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import {
  passwordRecoveryRequest,
  passwordRecoveryVerify,
  passwordRecoveryReset,
} from "../../services/api";
import { loginUser } from "../../redux/auth";
const PASSWORD_RULE = {
  validator: (_, value) => {
    if (!value) return Promise.resolve();
    const hasLen = value.length >= 8;
    const hasLower = /[a-z]/.test(value);
    const hasUpper = /[A-Z]/.test(value);
    const hasDigit = /\d/.test(value);
    const hasSpecial = /[#@$%^&*()_\-+=[\]{};':"\\|,.<>/?~]/.test(value);
    return hasLen && hasLower && hasUpper && hasDigit && hasSpecial
      ? Promise.resolve()
      : Promise.reject(
          new Error(
            "Пароль должен быть ≥ 8 символов и содержать строчные/прописные буквы, цифры и спецсимволы.",
          ),
        );
  },
};

const Login = () => {
  const [form] = Form.useForm();
  const [recForm] = Form.useForm();

  // модалка-предупреждение
  const [warningOpen, setWarningOpen] = useState(true);
  const [warningAcked, setWarningAcked] = useState(false);

  // восстановление
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const [serverToken, setServerToken] = useState(""); // токен из /request
  const [secondsLeft, setSecondsLeft] = useState(0); // таймер
  const [requestSuccess, setRequestSuccess] = useState(false); // алерт «код отправлен»
  const isCounting = secondsLeft > 0;
  const canResend = secondsLeft === 0 && !!recForm.getFieldValue("email");

  const handleAcknowledge = () => {
    setWarningAcked(true);
    setWarningOpen(false);
  };

  useEffect(() => {
    if (!isCounting) return;
    const t = setInterval(
      () => setSecondsLeft((s) => (s > 0 ? s - 1 : 0)),
      1000,
    );
    return () => clearInterval(t);
  }, [isCounting]);
  const dispatch = useDispatch();
  const onFinish = (values) => {
    dispatch(loginUser(values));
  };

  // запрос кода
  const onGetCode = async () => {
    try {
      const email = recForm.getFieldValue("email")?.trim();
      await recForm.validateFields(["email"]);
      setRequestLoading(true);
      const res = await passwordRecoveryRequest(email);
      if (!res?.token) {
        notification.error({
          message: "Не удалось отправить код",
          description: "Сервер не вернул токен подтверждения.",
        });
        return;
      }
      setServerToken(res.token);
      setSecondsLeft(60);
      setRequestSuccess(true);
      notification.success({
        message: "Код отправлен",
        description: "На вашу почту был отправлен код подтверждения.",
      });
    } catch (e) {
      // по ТЗ — текст про отсутствие пользователя
      notification.error({
        message: "Ошибка отправки кода",
        description:
          e?.message ||
          "По указанному электронной почте нет зарегистрированного пользователя",
      });
    } finally {
      setRequestLoading(false);
    }
  };

  // проверить код
  const verifyCode = async () => {
    const code = recForm.getFieldValue("code")?.trim();
    if (!serverToken || !code) return false;
    try {
      setVerifyLoading(true);
      await passwordRecoveryVerify({ token: serverToken, code });
      return true;
    } catch (e) {
      recForm.setFields([{ name: "code", errors: ["Неверный код"] }]);
      notification.error({
        message: "Неверный код подтверждения",
        description:
          e?.message || "Проверьте введённый код и попробуйте ещё раз.",
      });
      return false;
    } finally {
      setVerifyLoading(false);
    }
  };

  // сохранить новый пароль
  const onSaveNewPassword = async () => {
    try {
      await recForm.validateFields(); // email/код/пароли если поля уже показаны
      const p1 = recForm.getFieldValue("newPassword");
      const p2 = recForm.getFieldValue("confirm");
      if (p1 !== p2) {
        const msg =
          "Не совпадают пароли / Parólder sáıkes kelmeıdi / Passwords don't match";
        recForm.setFields([
          { name: "newPassword", errors: [msg] },
          { name: "confirm", errors: [msg] },
        ]);
        return;
      }

      const ok = await verifyCode();
      if (!ok) return;

      setSaveLoading(true);
      await passwordRecoveryReset({ token: serverToken, newPassword: p1 });

      Modal.success({
        title: "Пароль изменён",
        content: "Вы можете войти, используя новый пароль.",
        onOk: () => window.location.replace("/signin"),
      });

      // сбросить состояние блока восстановления
      setRecoveryOpen(false);
      setServerToken("");
      setSecondsLeft(0);
      setRequestSuccess(false);
      recForm.resetFields();
    } catch (e) {
      // если не заполнено — antd сам подсветит; иначе общий вывод
      if (!e?.errorFields) {
        notification.error({
          message: "Ошибка сохранения",
          description:
            e?.message || "Не удалось сбросить пароль. Попробуйте позже.",
        });
      }
    } finally {
      setSaveLoading(false);
    }
  };

  const confirmRule = useMemo(
    () => [
      { required: true, message: "Данное поле должно быть заполнено" },
      ({ getFieldValue }) => ({
        validator(_, value) {
          if (!value) return Promise.resolve();
          if (getFieldValue("newPassword") === value) return Promise.resolve();
          return Promise.reject(
            new Error(
              "Не совпадают пароли / Parólder sáıkes kelmeıdi / Passwords don't match",
            ),
          );
        },
      }),
    ],
    [],
  );

  return (
    <>
      {/* Предупреждение */}
      <Modal
        open={warningOpen}
        onCancel={() => {}}
        closable={false}
        maskClosable={false}
        centered
        title={
          <span>
            <ExclamationCircleFilled
              style={{ marginRight: 8, color: "#faad14" }}
            />
            Предупреждение
          </span>
        }
        footer={[
          <Button key="ok" type="primary" onClick={handleAcknowledge}>
            Согласен
          </Button>,
        ]}
      >
        <Typography.Paragraph style={{ marginBottom: 0 }}>
          Неправомерное использование учетных данных третьего лица при
          регистрации и авторизации в системе влечет ответственность в
          соответствии с законодательством РК.
        </Typography.Paragraph>
      </Modal>

      <Row style={{ paddingTop: 200 }}>
        <Col span={6} offset={9}>
          <h2 style={{ textAlign: "center", marginTop: 20 }}>
            Информационно-аналитическая система «Мониторинг и прогноз
            социально-экономических показателей»
          </h2>

          {/* форма входа: скрываем, когда открыт recovery */}
          {!recoveryOpen && (
            <Form
              form={form}
              onFinish={onFinish}
              className="login-form"
              autoComplete="off"
              layout="vertical"
            >
              <Form.Item
                name="username"
                rules={[
                  { required: true, message: "Пожалуйста, введите логин!" },
                ]}
              >
                <Input
                  disabled={!warningAcked}
                  size="large"
                  placeholder="Логин"
                  autoComplete="username"
                  prefix={<UserOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: "Пожалуйста, введите пароль!",
                  },
                  PASSWORD_RULE,
                ]}
              >
                <Input.Password
                  disabled={!warningAcked}
                  size="large"
                  placeholder="Пароль"
                  autoComplete="current-password"
                  prefix={<LockOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                disabled={!warningAcked}
              >
                Войти
              </Button>

              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button
                  type="link"
                  onClick={() => {
                    setRecoveryOpen(true);
                    recForm.resetFields();
                  }}
                >
                  Забыли пароль?
                </Button>
              </div>
            </Form>
          )}

          {/* восстановление пароля */}
          {recoveryOpen && (
            <div style={{ marginTop: 24, marginBottom: 24 }}>
              <Space style={{ marginBottom: 12 }}>
                <Button
                  type="primary"
                  icon={<ArrowLeftOutlined />}
                  onClick={() => {
                    setRecoveryOpen(false);
                    setServerToken("");
                    setSecondsLeft(0);
                    setRequestSuccess(false);
                    recForm.resetFields();
                  }}
                >
                  Назад к входу
                </Button>
              </Space>

              <Typography.Title
                level={4}
                style={{ marginBottom: 12, color: "#fff" }}
              >
                Восстановление пароля
              </Typography.Title>

              <Form form={recForm} layout="vertical" autoComplete="off">
                {/* Email */}
                <Form.Item
                  name="email"
                  label="Email для кода подтверждения"
                  rules={[
                    {
                      required: true,
                      message: "Данное поле должно быть заполнено",
                    },
                    { type: "email", message: "Некорректный e-mail" },
                  ]}
                >
                  <Input
                    size="large"
                    placeholder="example@domain.com"
                    autoComplete="email"
                    prefix={
                      <MailOutlined style={{ color: "rgba(0,0,0,.25)" }} />
                    }
                  />
                </Form.Item>

                {/* Кнопки под Email — Получить/Повторно + таймер */}
                <Space
                  style={{
                    width: "100%",
                    justifyContent: "space-between",
                    marginBottom: 12,
                  }}
                >
                  <Button
                    type="default"
                    onClick={onGetCode}
                    style={{
                      color: isCounting ? "#fff" : "#000",
                    }}
                    disabled={isCounting || requestLoading}
                    loading={requestLoading}
                  >
                    {isCounting
                      ? "Код отправлен"
                      : "Получить код подтверждения"}
                  </Button>

                  {isCounting ? (
                    <Typography.Text
                      type="secondary"
                      style={{
                        color: isCounting ? "#fff" : "#000",
                      }}
                    >
                      Введите код за {secondsLeft} сек
                    </Typography.Text>
                  ) : (
                    <Button
                      icon={<ReloadOutlined />}
                      type={"primary"}
                      style={{ color: !canResend ? "#fff" : "#000" }}
                      onClick={onGetCode}
                      disabled={!canResend}
                    >
                      Отправить код повторно
                    </Button>
                  )}
                </Space>

                {/* инфо-алерт после отправки кода */}
                {requestSuccess && (
                  <Alert
                    type="success"
                    showIcon
                    message="На вашу почту был отправлен код подтверждения."
                    style={{ marginBottom: 16 }}
                  />
                )}

                {/* Остальные поля показываем ТОЛЬКО после успешной отправки кода */}
                {serverToken && (
                  <>
                    <Form.Item
                      name="code"
                      label="Введите код из письма"
                      rules={[
                        {
                          required: true,
                          message: "Данное поле должно быть заполнено",
                        },
                        { len: 4, message: "Код состоит из 4 цифр" },
                      ]}
                    >
                      <Input
                        size="large"
                        placeholder="4-значный код"
                        maxLength={4}
                        prefix={
                          <FieldNumberOutlined
                            style={{ color: "rgba(0,0,0,.25)" }}
                          />
                        }
                      />
                    </Form.Item>

                    <Form.Item
                      name="newPassword"
                      label="Новый пароль"
                      rules={[
                        {
                          required: true,
                          message: "Данное поле должно быть заполнено",
                        },
                        PASSWORD_RULE,
                      ]}
                      hasFeedback
                    >
                      <Input.Password
                        size="large"
                        placeholder="Не менее 8 символов"
                        autoComplete="new-password"
                        prefix={
                          <LockOutlined style={{ color: "rgba(0,0,0,.25)" }} />
                        }
                      />
                    </Form.Item>

                    <Form.Item
                      name="confirm"
                      label="Повторите пароль"
                      dependencies={["newPassword"]}
                      rules={confirmRule}
                      hasFeedback
                    >
                      <Input.Password
                        size="large"
                        placeholder="Повторите пароль"
                        autoComplete="new-password"
                        prefix={
                          <LockOutlined style={{ color: "rgba(0,0,0,.25)" }} />
                        }
                      />
                    </Form.Item>

                    <Button
                      type="primary"
                      block
                      onClick={onSaveNewPassword}
                      loading={verifyLoading || saveLoading}
                    >
                      Сохранить
                    </Button>
                  </>
                )}
              </Form>
            </div>
          )}
        </Col>
      </Row>
    </>
  );
};

export default Login;
